import React, { useContext, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Breadcrumb } from 'antd'
import { useManualQuery } from 'graphql-hooks'
import orderBy from 'lodash/orderBy'
import { getAddonsSubscription, getLocations } from '../../../../graphql/custom-queries'
import { notificationStore } from '../../../../context/notificationsContext/store'
import * as logger from '../../../../services/logger'
import LinkButton from '../../../../components/link-button'

import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { selectFeatureFlagsSlice } from '../../../../store/feature-flags-slice'
import { useAppSelector } from '../../../../store/hooks'

import IntlMessages from '../../../../util/IntlMessages'
import LocationCard from '../../../../components/location-card'
import CircularProgress from '../../../../components/circular-progress'
import { NUMBER_OF_LOCATIONS_LIMIT } from '@vacationtracker/shared/data/app-parameters'

import { IGetLocationsData, IGetLocations } from '../../../../types/locations'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { FrontendUrls } from '../../../../types/urls'
import { IGetAddonsSubscriptionData } from '../../../../types/custom-queries'
import { AddonTypeEnum } from '@vacationtracker/shared/types/addons'
import CoreCompleteTag from '../../../../components/core-complete-tag'
import FreemiumPricingTooltip from '../../../../components/freemium-pricing-tooltip'

const LocationsPage = () => {
  const { actionNotifications } = useContext(notificationStore)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)

  const [isLoading, setIsLoading] = useState(true)
  const [defaultLocationExists] = useState(false)
  const [showRenameWarning, setShowRenameWarning] = useState(false)
  const [locationList, setLocationList] = useState<IGetLocations[]>([])
  const [allowUnlimitedLocations, setAllowUnlimitedLocations] = useState(false)

  const [ getLocationsQuery ] = useManualQuery<IGetLocationsData>(getLocations)
  const [ addonsQuery ] = useManualQuery<IGetAddonsSubscriptionData>(getAddonsSubscription)

  useEffect(() => {
    fetchLocationList()
  }, [])

  useEffect(() => {
    fetchLocationList()
  }, [actionNotifications])

  const fetchLocationList = async () => {
    try {
      const response = await getLocationsQuery()
      if (!response.data || response.error) throw response.error

      if (authCompany?.plan === SubscriptionPlanEnum.core) {
        const addonsResponse = await addonsQuery()
        if (addonsResponse.data?.getAddonsSubscription) {
          setAllowUnlimitedLocations(() => {
            return addonsResponse.data?.getAddonsSubscription?.addons.find(addon => addon.type === AddonTypeEnum.NO_LIMIT_LOCATION)?.status === 'ACTIVE'
          })
        }
      }

      setLocationList(orderBy(response.data.getLocationList, [l => l.isDefault,'name'], ['desc', 'asc']))
      setIsLoading(false)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    } catch (err) { logger.error('error fetching location list', err) }
  }

  useEffect(() => {
    const shouldRename = locationList.find(location => location.name.includes('<Autodetected for Departments'))
    if (shouldRename) {
      setShowRenameWarning(true)
    }
  }, [locationList])

  const shouldDisableCreatingNewLocation = (companyFeatureFlags, companyPlan, locationCount, numberOfLocationsLimit, allowUnlimited) => {
    if (companyFeatureFlags.includes(FeatureFlagEnum.noLimitLocation)) {
      return false
    }

    // Freemium plan is not allowed to create locations
    if (companyPlan === SubscriptionPlanEnum.freemium) return true

    return companyPlan === SubscriptionPlanEnum.core && locationCount >= numberOfLocationsLimit && !allowUnlimited
  }

  return (
    <div className='main-content'>
      <div className="main-content-header">
        <div className="main-content-header-title">
          <span><IntlMessages id="app.locations" /></span>
        </div>
        <div className="main-content-header-breadcrumb">
          <Breadcrumb
            items={[
              {
                title: <Link to={FrontendUrls.dashboard}><IntlMessages id="sidebar.dashboard" /></Link>,
              },
              {
                title: <IntlMessages id="sidebar.section.leaveSetupLowercase" />,
              },
              {
                title: <IntlMessages id="app.locations" />,
              },
            ]}
          />
        </div>
      </div>
      <div className="main-content-body">
        <div className="locations-body">
          <div className="btn-actions">
            <FreemiumPricingTooltip
              title={<IntlMessages id="components.freemiumPricingTooltip.multipleLocations" />}
              isFreemiumPlan={authCompany?.plan === SubscriptionPlanEnum.freemium}
              freemiumData={[
                'components.freemiumPricingTooltip.oneLocation',
                'components.freemiumPricingTooltip.oneLeaveType',
              ]}
              coreData={[
                'components.freemiumPricingTooltip.upTo3Locations',
                'components.freemiumPricingTooltip.unlimitedLeaveTypes',
              ]}
              completeData={[
                'components.freemiumPricingTooltip.unlimitedLocations',
                'components.freemiumPricingTooltip.allCoreFeatures',
              ]}
              featureName="create_location"
              featureCategory="locations"
            >
              <LinkButton 
                size='large'
                to="/app/settings/locations/create"
                disabled={shouldDisableCreatingNewLocation(featureFlags, authCompany?.plan, locationList.length, NUMBER_OF_LOCATIONS_LIMIT, allowUnlimitedLocations)}
              >
                <IntlMessages id="locations.createNewLocation" />
                {authCompany?.plan === SubscriptionPlanEnum.freemium && <CoreCompleteTag />}
              </LinkButton>
            </FreemiumPricingTooltip>
          </div>
          {isLoading ? <CircularProgress /> :
            <div>
              {showRenameWarning &&
                <Alert
                  key="warning"
                  message={<IntlMessages id="locations.pleaseRenameLocationName" />}
                  description={<IntlMessages id="locations.weAutomaticallyCreatedLocations" />}
                  type="warning"
                  showIcon
                  style={{ marginBottom: '20px' }}
                />
              }
              {locationList.map((location, index) =>
                <LocationCard
                  key={location.id}
                  isDefault={location.isDefault}
                  quickTourLocation={defaultLocationExists ? location.isDefault : (index === 0)} // If there is no default location, use the first one for the quick tour
                  id={location.id}
                  title={location.name}
                  users={location.users}
                />
              )}
              {/* freemium plan does not need this warning since it has upsell labels */}
              {[SubscriptionPlanEnum.core].includes(authCompany?.plan as SubscriptionPlanEnum) && 
               shouldDisableCreatingNewLocation(featureFlags, authCompany?.plan, locationList.length, NUMBER_OF_LOCATIONS_LIMIT, allowUnlimitedLocations) &&
                <Alert
                  style={{ marginBottom: 20 }}
                  message={
                    <IntlMessages id="locations.locationLimitReached"
                      values={{
                        limitNumber: NUMBER_OF_LOCATIONS_LIMIT,
                        billingPage: (...chunks) => (<Link to="/app/settings/billing">{chunks}</Link>),
                      }}
                    />}
                  type="warning"
                />
              }
            </div>
          }
        </div>
      </div>
    </div>
  )
}

export default LocationsPage
